import React, { useState } from "react";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Button, Stack, TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Toast } from "./Toast";
import { Connection } from "../sockets-client";
import { absoluteJoinUrl } from "../utils/joinPath";
import { usePostHog } from "@posthog/react";

type DropdownOptions = {
  value: string;
  name: string;
};

type Props = {
  label: string;
  options: DropdownOptions[];
  isInputTextField: Boolean;
  socketConnection: Connection;
  /** When the server assigns a room (create flow); parent listens on the socket. */
  hostedRoomId?: string | null;
  /** Namespace path e.g. `/addata` — used for invite links. */
  namespacePath?: string | null;
  /** Literature MVP: no blind/bot players. */
  hideBots?: boolean;
};

export const Menu = ({
  label,
  options,
  isInputTextField,
  socketConnection,
  hostedRoomId,
  namespacePath,
  hideBots = false,
}: Props) => {
  const posthog = usePostHog();
  const [value, setValue] = useState("");
  const [botCount, setBotCount] = useState("0");
  const [isError, setIsError] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const handleChange = (event: SelectChangeEvent) => {
    setValue(event.target.value as string);
    setBotCount("0");
  };

  const resetToastVisibility = () => {
    setIsError(!isError);
  };

  const onCreateClick = (event: any) => {
    event.preventDefault();
    if (value.length === 0) {
      setIsError(true);
    } else {
      posthog?.capture("room_created", { player_count: Number(value), bot_count: hideBots ? 0 : Number(botCount), namespace: namespacePath });
      socketConnection.createRoom(Number(value), hideBots ? 0 : Number(botCount));
    }
  };

  const onJoinClickRoomByIdEntry = (event: any) => {
    event.preventDefault();
    if (value === undefined || value.length === 0) {
      setIsError(true);
    } else {
      posthog?.capture("room_joined", { room_id: value, namespace: namespacePath });
      socketConnection.joinRoom(value);
    }
  };

  const onJoinClickHostedRoom = (event: any) => {
    event.preventDefault();
    if (!hostedRoomId || hostedRoomId.length === 0) {
      setIsError(true);
    } else {
      socketConnection.joinRoom(hostedRoomId);
    }
  };

  const getBotOptions = (): DropdownOptions[] => {
    const roomSize = Number(value);
    if (!roomSize || roomSize < 2) return [{ value: "0", name: "0 (no bots)" }];
    const opts: DropdownOptions[] = [];
    for (let i = 0; i <= roomSize - 1; i++) {
      opts.push({ value: String(i), name: i === 0 ? "0 (no bots)" : String(i) });
    }
    return opts;
  };

  const inviteUrl =
    hostedRoomId && namespacePath ? absoluteJoinUrl(namespacePath, hostedRoomId) : "";

  const copyInviteLink = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopyFeedback(true);
      posthog?.capture("invite_link_copied", { room_id: hostedRoomId, namespace: namespacePath });
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      setIsError(true);
    }
  };

  const showComponent = () => {
    if (isInputTextField) {
      return (
        <>
          <FormControl fullWidth size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="addata-room-menu-label">{label}</InputLabel>
            <Select
              labelId="addata-room-menu-label"
              value={value}
              label={label}
              onChange={handleChange}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {value.length > 0 && !hideBots && (
            <FormControl fullWidth size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="addata-blinds-menu-label">Blinds</InputLabel>
              <Select
                labelId="addata-blinds-menu-label"
                value={botCount}
                label="Blinds"
                onChange={(e: SelectChangeEvent) => setBotCount(e.target.value)}
              >
                {getBotOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      );
    }
    return (
      <TextField
        color="info"
        name="name"
        label="Room ID"
        type="text"
        fullWidth
        size="small"
        sx={{ "& .MuiInputBase-root": { minHeight: 46 } }}
        onChange={(v) => setValue(v.target.value)}
      />
    );
  };

  const showRoomIDComponent = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1.5}>
          <Typography variant="h6" textAlign="center">
            Your room ID
          </Typography>
          <Typography
            variant="h4"
            textAlign="center"
            sx={{ letterSpacing: 2 }}
            data-testid="hosted-room-id"
          >
            {hostedRoomId}
          </Typography>
          {inviteUrl ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                {inviteUrl}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={copyInviteLink}
                sx={{ minHeight: 46, fontWeight: 600 }}
              >
                {copyFeedback ? "Copied!" : "Copy invite link"}
              </Button>
            </>
          ) : null}
          <Button sx={{ minHeight: 46, fontWeight: 700 }} variant="contained" color="primary" onClick={onJoinClickHostedRoom}>
            Join this room
          </Button>
        </Stack>
      </Box>
    );
  };

  const showChoiceComponent = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1.5}>
          {showComponent()}
          <Button
            variant="contained"
            color={!isInputTextField ? "primary" : "secondary"}
            onClick={!isInputTextField ? onJoinClickRoomByIdEntry : onCreateClick}
            sx={{ minHeight: 46, fontWeight: 700 }}
          >
            {!isInputTextField ? "Join" : "Create"}
          </Button>
        </Stack>
        {isError ? (
          <Toast
            severity={"error"}
            message={isInputTextField ? "Please select an option" : "Please enter a code"}
            onClose={resetToastVisibility}
          />
        ) : null}
      </Box>
    );
  };

  if (hostedRoomId) {
    return showRoomIDComponent();
  }
  return showChoiceComponent();
};
